import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import FavButton from './FavButton';

type RelatedProps = {
  current: {
    domain: string;
    tags?: string | string[];
    bundle?: string;
    tld?: string;
  };
  pool: Array<{
    domain: string;
    tags?: string | string[];
    bundle?: string;
    tld?: string;
    bin_price?: number;
  }>;
  limit?: number;
  className?: string;
};

export default function RelatedDomains({ current, pool, limit = 6, className = '' }: RelatedProps) {
  const relatedDomains = useMemo(() => {
    if (!pool?.length) return [];

    // Normalize current domain tags
    const currentTagsArr = Array.isArray(current.tags)
      ? current.tags
      : String(current.tags ?? '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);

    // Score and filter candidates
    const candidates = pool
      .filter((domain) => domain.domain !== current.domain) // Exclude current domain
      .map((domain) => {
        let score = 0;

        // Normalize candidate tags
        const candidateTagsArr = Array.isArray(domain.tags)
          ? domain.tags
          : String(domain.tags ?? '')
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean);

        // +2 if shares any tag
        if (currentTagsArr.some((tag) => candidateTagsArr.includes(tag))) {
          score += 2;
        }

        // +1 if same bundle
        if (current.bundle && domain.bundle === current.bundle) {
          score += 1;
        }

        // +1 if same TLD
        if (current.tld && domain.tld === current.tld) {
          score += 1;
        }

        return { ...domain, score };
      })
      .filter((domain) => domain.score > 0) // Only include domains with some relevance
      .sort((a, b) => {
        // Sort by score desc, then by price asc, then alphabetically
        if (a.score !== b.score) return b.score - a.score;
        if (a.bin_price && b.bin_price && a.bin_price !== b.bin_price) {
          return a.bin_price - b.bin_price;
        }
        return a.domain.localeCompare(b.domain);
      })
      .slice(0, limit);

    return candidates;
  }, [current, pool, limit]);

  if (!relatedDomains.length) {
    return null;
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section className={`${className}`} aria-labelledby="related-domains-heading">
      <h2
        id="related-domains-heading"
        className="text-3xl font-bold text-foreground mb-8 text-center"
      >
        Similar domains you might like
      </h2>

      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {relatedDomains.map((domain) => (
          <li key={domain.domain}>
            <Link to={`/domain/${domain.domain}`} className="block">
              <Card className="apple-card h-full hover:shadow-lg transition-shadow duration-200 cursor-pointer relative">
                {/* Favorite Button */}
                <div className="absolute top-3 right-3 z-10">
                  <FavButton domain={domain.domain} size="sm" variant="ghost" />
                </div>

                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex-grow">
                    <h3 className="text-lg font-bold text-foreground mb-2">{domain.domain}</h3>

                    {domain.bin_price && (
                      <p className="text-sm text-muted-foreground mb-4">
                        {formatPrice(domain.bin_price)}
                      </p>
                    )}
                  </div>

                  <div className="mt-auto inline-flex items-center text-sm font-medium text-primary">
                    View Details →
                  </div>
                </CardContent>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
